import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';
import { api } from '../../api.js';
import { NumStat, TextStat } from './DeltaStat.js';
import { Badge } from '@/components/ui/badge';
import { textureVerdict, verdictTone } from '../budget.js';

interface ImageOverlayDiffProps {
  depotPath: string;
  leftRev: number;
  rightRev: number;
  // For native image files (.png, .jpg, …) we want the raw file at
  // the rev (cheap p4 print). For .uasset textures we want the
  // rendered preview.webp from the DAM cache.
  source: 'preview' | 'raw';
}

type Mode = 'slider' | 'side-by-side';

export function ImageOverlayDiff({
  depotPath,
  leftRev,
  rightRev,
  source,
}: ImageOverlayDiffProps) {
  const [pct, setPct] = useState(50);
  const [mode, setMode] = useState<Mode>('slider');
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (mode !== 'slider') return;
    const el = containerRef.current;
    if (!el) return;
    let dragging = false;
    function setFromEvent(e: PointerEvent): void {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
      setPct(Math.round((x / rect.width) * 100));
    }
    function down(e: PointerEvent): void {
      dragging = true;
      setFromEvent(e);
      el?.setPointerCapture(e.pointerId);
    }
    function move(e: PointerEvent): void {
      if (dragging) setFromEvent(e);
    }
    function up(e: PointerEvent): void {
      dragging = false;
      el?.releasePointerCapture(e.pointerId);
    }
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    return () => {
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
    };
  }, [mode]);

  const urlFor = (rev: number): string =>
    source === 'raw' ? api.fileUrl(depotPath, rev) : api.damPreviewUrl(depotPath, rev);

  const leftUrl = urlFor(leftRev);
  const rightUrl = urlFor(rightRev);

  return (
    <div className="flex flex-col gap-2">
      {/* uasset textures carry their authored resolution / format / mips
          in the cached properties JSON; surface a delta strip. Raw image
          files have no such JSON, so the strip self-hides for them. */}
      {source === 'preview' && (
        <TextureStatsStrip depotPath={depotPath} leftRev={leftRev} rightRev={rightRev} />
      )}
      <div className="flex flex-nowrap items-center justify-between">
        <ModeToggle value={mode} onChange={setMode} />
        {mode === 'slider' && (
          <span className="text-xs text-muted-foreground">
            Drag anywhere on the image
          </span>
        )}
      </div>

      {mode === 'slider' ? (
        <div>
          <div
            ref={containerRef}
            className="relative w-full cursor-ew-resize select-none overflow-hidden rounded-md border border-neutral-800"
            style={{
              touchAction: 'none',
              background:
                'repeating-conic-gradient(#1a1f29 0% 25%, #0d1117 0% 50%) 50% / 16px 16px',
            }}
          >
            <img
              src={rightUrl}
              alt={`#${rightRev}`}
              draggable={false}
              className="block h-auto w-full"
            />
            <img
              src={leftUrl}
              alt={`#${leftRev}`}
              draggable={false}
              className="absolute inset-0 h-full w-full object-fill"
              style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }}
            />
            <div
              className="pointer-events-none absolute bottom-0 top-0 w-0.5 bg-white/85"
              style={{ left: `${pct}%`, transform: 'translateX(-1px)' }}
            />
            <div
              className="pointer-events-none absolute rounded-sm bg-white/95 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-black shadow"
              style={{
                top: '50%',
                left: `${pct}%`,
                transform: 'translate(-50%, -50%)',
              }}
            >
              ↔
            </div>
            <RevTag rev={leftRev} side="left" />
            <RevTag rev={rightRev} side="right" />
          </div>
          <Slider
            min={0}
            max={100}
            value={pct}
            onChange={(e) => setPct(parseInt(e.currentTarget.value, 10))}
            className="mt-2"
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <SidePanel rev={leftRev} url={leftUrl} />
          <SidePanel rev={rightRev} url={rightUrl} />
        </div>
      )}
    </div>
  );
}

function ModeToggle({ value, onChange }: { value: Mode; onChange: (v: Mode) => void }) {
  const opts: { v: Mode; label: string }[] = [
    { v: 'slider', label: 'Slider' },
    { v: 'side-by-side', label: 'Side by side' },
  ];
  return (
    <div
      role="group"
      aria-label="Diff mode"
      className="inline-flex overflow-hidden rounded-sm border border-neutral-800 bg-neutral-950"
    >
      {opts.map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          className={cn(
            'px-3 py-1 text-xs transition-colors',
            value === o.v
              ? 'bg-neutral-800 text-foreground'
              : 'text-muted-foreground hover:bg-neutral-900 hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function RevTag({ rev, side }: { rev: number; side: 'left' | 'right' }) {
  return (
    <div
      className={cn(
        'pointer-events-none absolute top-2 rounded-sm bg-black/65 px-1.5 py-0.5 font-mono text-[11px] text-white',
        side === 'left' ? 'left-2' : 'right-2',
      )}
    >
      #{rev}
    </div>
  );
}

// ----- Texture stats strip ---------------------------------------------------

interface TexStats {
  sizeX?: number | undefined;
  sizeY?: number | undefined;
  format?: string | undefined;
  numMips?: number | undefined;
  lodGroup?: string | undefined;
  compression?: string | undefined;
}

// Pull authored texture stats out of the UTexture2D properties dump.
// The real resolution + source format live under the export's
// `Properties.Source` (the cooked top-level SizeX/PixelFormat are 0 /
// PF_Unknown for UEFN source assets). Returns null for non-textures
// (e.g. a material's preview JSON), which self-hides the strip.
function textureStatsOf(doc: unknown): TexStats | null {
  const exports = Array.isArray(doc) ? doc : null;
  if (!exports) return null;
  for (const e of exports) {
    if (typeof e !== 'object' || e === null) continue;
    const props = (e as Record<string, unknown>).Properties;
    if (typeof props !== 'object' || props === null) continue;
    const p = props as Record<string, unknown>;
    const src = p.Source;
    if (typeof src !== 'object' || src === null) continue;
    const s = src as Record<string, unknown>;
    if (typeof s.SizeX !== 'number' || typeof s.SizeY !== 'number') continue;
    return {
      sizeX: s.SizeX,
      sizeY: s.SizeY,
      format: strOr(s.Format),
      numMips: typeof s.NumMips === 'number' ? s.NumMips : undefined,
      lodGroup: strOrNone(p.LODGroup),
      compression: strOrNone(p.CompressionSettings),
    };
  }
  return null;
}

function strOr(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}
// UE serializes an unset enum property as the literal "None" — treat it
// as absent so we don't render a noisy "None" chip.
function strOrNone(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 && v !== 'None' ? v : undefined;
}

async function fetchPreviewJson(depotPath: string, rev: number): Promise<unknown | null> {
  const res = await fetch(api.damPreviewUrl(depotPath, rev, 'json'), { credentials: 'include' });
  if (res.status !== 200) return null;
  const ct = res.headers.get('content-type') ?? '';
  if (!ct.includes('json')) return null;
  const body = await res.json();
  if (typeof body === 'object' && body && 'noPreview' in body) return null;
  return body;
}

function TextureStatsStrip({
  depotPath,
  leftRev,
  rightRev,
}: {
  depotPath: string;
  leftRev: number;
  rightRev: number;
}) {
  // Same queryKey as JsonPropertyDiff / BlueprintGraphDiff so the JSON is
  // fetched once and shared across whichever diff views are mounted.
  const leftQ = useQuery({
    queryKey: ['dam.preview.json', depotPath, leftRev],
    queryFn: () => fetchPreviewJson(depotPath, leftRev),
  });
  const rightQ = useQuery({
    queryKey: ['dam.preview.json', depotPath, rightRev],
    queryFn: () => fetchPreviewJson(depotPath, rightRev),
  });
  const left = textureStatsOf(leftQ.data);
  const right = textureStatsOf(rightQ.data);
  if (!left && !right) return null; // not a texture (or stats unavailable)

  const res = (s: TexStats | null): string | undefined =>
    s && s.sizeX != null && s.sizeY != null ? `${s.sizeX}×${s.sizeY}` : undefined;

  // Budget flag on the current (right) rev's authored resolution.
  const cur = right ?? left;
  const maxDim = cur && cur.sizeX != null && cur.sizeY != null ? Math.max(cur.sizeX, cur.sizeY) : null;
  const verdict = maxDim !== null ? textureVerdict(maxDim) : null;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <TextStat label="Resolution" left={res(left)} right={res(right)} />
      {verdict && verdict !== 'ok' && (
        <Badge tone={verdictTone(verdict)}>{verdict === 'over' ? '⚠ over budget' : 'large'}</Badge>
      )}
      <TextStat label="Format" left={left?.format} right={right?.format} />
      <NumStat label="Mips" left={left?.numMips} right={right?.numMips} />
      {(left?.compression || right?.compression) && (
        <TextStat label="Compression" left={left?.compression} right={right?.compression} />
      )}
      {(left?.lodGroup || right?.lodGroup) && (
        <TextStat label="LOD group" left={left?.lodGroup} right={right?.lodGroup} />
      )}
    </div>
  );
}

function SidePanel({ rev, url }: { rev: number; url: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-center text-xs text-muted-foreground">#{rev}</span>
      <div
        className="overflow-hidden rounded-md border border-neutral-800"
        style={{
          background:
            'repeating-conic-gradient(#1a1f29 0% 25%, #0d1117 0% 50%) 50% / 16px 16px',
        }}
      >
        <img src={url} alt={`#${rev}`} className="block h-auto w-full" />
      </div>
    </div>
  );
}
